// ─────────────────────────────────────────────────────────────────────────────
// stroke_geometry.ts — Mathematical shape analysis for spell classification
//
// Provides: equidistant resampling, turning function θ(s), and a complete
// structural feature vector.  Pure functions, no external state.
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokePoint, BoundingBox, RegionBounds, Point } from '../shared/notebook_model.js';

// ── Constants ────────────────────────────────────────────────────────────────

const RESAMPLE_N = 64;
const TWO_PI = 2 * Math.PI;
const LASSO_CLOSE_PX = 40;
const MIN_LASSO_POINTS = 8;
const LASSO_MIN_AREA = 200;

// ── Resampling ───────────────────────────────────────────────────────────────

/**
 * Resample a polyline to N equidistant points along its arc.
 * Preserves start and end; interpolates pressure and timestamp linearly.
 */
export function resample(pts: StrokePoint[], N: number = RESAMPLE_N): StrokePoint[] {
  if (pts.length < 2 || N < 2) return pts.slice();

  const total = pathLength(pts);
  if (total < 1e-6) return [pts[0]];

  const spacing = total / (N - 1);
  const out: StrokePoint[] = [pts[0]];
  let carry = 0;

  for (let i = 1; i < pts.length && out.length < N; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    const segLen = Math.sqrt(dx * dx + dy * dy);
    if (segLen < 1e-9) continue;

    let consumed = 0;
    while (consumed + (spacing - carry) <= segLen + 1e-9 && out.length < N) {
      consumed += spacing - carry;
      const t = Math.min(consumed / segLen, 1);
      out.push(lerp(pts[i - 1], pts[i], t));
      carry = 0;
    }
    carry += segLen - consumed;
  }

  while (out.length < N) out.push(pts[pts.length - 1]);
  return out;
}

function lerp(a: StrokePoint, b: StrokePoint, t: number): StrokePoint {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    pressure: a.pressure + (b.pressure - a.pressure) * t,
    tiltX: a.tiltX, tiltY: a.tiltY, twist: a.twist,
    timestamp: a.timestamp + (b.timestamp - a.timestamp) * t,
  };
}

// ── Scalar path metrics ──────────────────────────────────────────────────────

export function pathLength(pts: StrokePoint[]): number {
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    len += Math.sqrt(dx * dx + dy * dy);
  }
  return len;
}

export function displacement(pts: StrokePoint[]): number {
  if (pts.length < 2) return 0;
  const dx = pts[pts.length - 1].x - pts[0].x;
  const dy = pts[pts.length - 1].y - pts[0].y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function bbox(pts: StrokePoint[]): BoundingBox {
  let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
  for (const p of pts) {
    if (p.x < mnX) mnX = p.x;
    if (p.y < mnY) mnY = p.y;
    if (p.x > mxX) mxX = p.x;
    if (p.y > mxY) mxY = p.y;
  }
  return { x: mnX, y: mnY, width: mxX - mnX, height: mxY - mnY };
}

// ── Turning function θ(s) ────────────────────────────────────────────────────
//
// θ[k] = cumulative signed angle change from segment 0 through segment k.
// A full circle yields θ ≈ ±2π.  A straight line yields θ ≈ 0.

export function turningFunction(pts: StrokePoint[]): Float64Array {
  const n = pts.length - 1;          // segment count
  if (n < 2) return new Float64Array(0);

  const theta = new Float64Array(n);
  theta[0] = 0;                       // reference direction is the first segment

  for (let i = 1; i < n; i++) {
    const cur  = Math.atan2(pts[i + 1].y - pts[i].y, pts[i + 1].x - pts[i].x);
    const prev = Math.atan2(pts[i].y - pts[i - 1].y, pts[i].x - pts[i - 1].x);
    theta[i] = theta[i - 1] + wrapAngle(cur - prev);
  }
  return theta;
}

/** Wrap to (−π, π]. */
function wrapAngle(a: number): number {
  while (a > Math.PI)  a -= TWO_PI;
  while (a < -Math.PI) a += TWO_PI;
  return a;
}

// ── Turning-function summary statistics ──────────────────────────────────────

export interface TurnStats {
  totalTurn: number;       // θ[last] — net angular displacement
  windingNumber: number;   // totalTurn / 2π (signed)
  absWinding: number;      // |windingNumber|
  turnVariance: number;    // var(dθ) — curvature regularity
  monotonicRatio: number;  // fraction of dθ sharing the sign of totalTurn
  peakAmplitude: number;   // max |θ| over the curve
}

export function turnStats(theta: Float64Array): TurnStats {
  if (theta.length < 2) {
    return { totalTurn: 0, windingNumber: 0, absWinding: 0, turnVariance: 0, monotonicRatio: 1, peakAmplitude: 0 };
  }
  const totalTurn = theta[theta.length - 1];
  const sign = totalTurn >= 0 ? 1 : -1;

  let sumDelta = 0, sumDelta2 = 0, monoCount = 0;
  let peak = 0;
  const m = theta.length - 1;           // number of dθ steps

  for (let i = 1; i < theta.length; i++) {
    const d = theta[i] - theta[i - 1];
    sumDelta  += d;
    sumDelta2 += d * d;
    if (d * sign >= 0) monoCount++;
    if (Math.abs(theta[i]) > peak) peak = Math.abs(theta[i]);
  }

  const meanD = sumDelta / m;
  const turnVariance = sumDelta2 / m - meanD * meanD;

  return {
    totalTurn,
    windingNumber: totalTurn / TWO_PI,
    absWinding: Math.abs(totalTurn / TWO_PI),
    turnVariance: Math.max(turnVariance, 0),
    monotonicRatio: monoCount / m,
    peakAmplitude: peak,
  };
}

// ── Self-crossings ───────────────────────────────────────────────────────────

export function countSelfCrossings(pts: StrokePoint[], cap: number = 8): number {
  let count = 0;
  const skip = 3;                      // adjacent segments can't cross meaningfully
  for (let i = skip; i < pts.length - 1; i++) {
    for (let j = 0; j < i - skip; j++) {
      if (segsCross(pts[i], pts[i + 1], pts[j], pts[j + 1])) {
        if (++count >= cap) return cap;
      }
    }
  }
  return count;
}

function segsCross(a1: StrokePoint, a2: StrokePoint, b1: StrokePoint, b2: StrokePoint): boolean {
  const rx = a2.x - a1.x, ry = a2.y - a1.y;
  const sx = b2.x - b1.x, sy = b2.y - b1.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-10) return false;
  const qpx = b1.x - a1.x, qpy = b1.y - a1.y;
  const t = (qpx * sy - qpy * sx) / denom;
  const u = (qpx * ry - qpy * rx) / denom;
  return t > 0.05 && t < 0.95 && u > 0.05 && u < 0.95;
}

// ── Lasso / closure detection ────────────────────────────────────────────────

export interface LassoData {
  isClosed: boolean;
  closingIndex: number;
  area: number;                // shoelace area (px²)
  bounds: RegionBounds | null;
}

/**
 * Walk backward from the end looking for the earliest point within
 * LASSO_CLOSE_PX of the endpoint.  If found, compute the shoelace area
 * of the enclosed loop.
 */
export function detectLasso(pts: StrokePoint[]): LassoData {
  const noLasso: LassoData = { isClosed: false, closingIndex: -1, area: 0, bounds: null };

  if (pts.length < MIN_LASSO_POINTS) return noLasso;

  const end = pts[pts.length - 1];
  const searchStart = Math.floor(pts.length * 0.2);

  for (let i = searchStart; i < pts.length - 4; i++) {
    const dx = end.x - pts[i].x;
    const dy = end.y - pts[i].y;
    if (Math.sqrt(dx * dx + dy * dy) < LASSO_CLOSE_PX) {
      const loop = pts.slice(i);
      const area = Math.abs(shoelace(loop));
      if (area <= LASSO_MIN_AREA) continue;

      let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
      for (const p of loop) {
        if (p.x < mnX) mnX = p.x;
        if (p.y < mnY) mnY = p.y;
        if (p.x > mxX) mxX = p.x;
        if (p.y > mxY) mxY = p.y;
      }

      return {
        isClosed: true,
        closingIndex: i,
        area,
        bounds: {
          points: pts.map(p => ({ x: p.x, y: p.y })),
          bbox: { x: mnX, y: mnY, width: mxX - mnX, height: mxY - mnY },
        },
      };
    }
  }
  return noLasso;
}

function shoelace(pts: StrokePoint[]): number {
  let a = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    a += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  return a / 2;
}

// ── Full feature vector ──────────────────────────────────────────────────────

export interface StrokeFeatures {
  // ── raw metrics
  arcLength: number;
  disp: number;
  straightness: number;    // disp / arcLength  (1 = perfectly straight)
  closureRatio: number;    // 1 − straightness  (1 = closed loop)

  // ── bounding box
  bbox: BoundingBox;
  bboxAspect: number;      // width / height

  // ── direction
  netDirection: number;    // atan2(end − start)

  // ── turning function
  turn: TurnStats;

  // ── topology
  selfCrossings: number;
  lasso: LassoData;

  // ── convenience refs
  startPoint: StrokePoint;
  endPoint: StrokePoint;
  resampled: StrokePoint[];
  raw: StrokePoint[];
}

/**
 * One-shot extraction of every feature the classifier needs.
 * Resamples to RESAMPLE_N points, computes turning function + stats,
 * self-crossings, and lasso geometry.
 */
export function extractFeatures(raw: StrokePoint[]): StrokeFeatures {
  const pts = resample(raw, RESAMPLE_N);
  const arc = pathLength(pts);
  const disp = displacement(pts);
  const box = bbox(pts);
  const theta = turningFunction(pts);
  const ts = turnStats(theta);
  const crossings = countSelfCrossings(pts);
  const lasso = detectLasso(raw);     // lasso uses raw points for accuracy

  return {
    arcLength: arc,
    disp,
    straightness: arc > 1e-6 ? disp / arc : 1,
    closureRatio: arc > 1e-6 ? 1 - disp / arc : 0,
    bbox: box,
    bboxAspect: box.height > 0 ? box.width / box.height : 1,
    netDirection: pts.length >= 2
      ? Math.atan2(pts[pts.length - 1].y - pts[0].y, pts[pts.length - 1].x - pts[0].x)
      : 0,
    turn: ts,
    selfCrossings: crossings,
    lasso,
    startPoint: raw[0],
    endPoint: raw[raw.length - 1],
    resampled: pts,
    raw,
  };
}

// ═════════════════════════════════════════════════════════════════════════════
// Shape Recognition Engine
//
// Analyses a committed stroke and attempts to recognise a geometric primitive.
// Returns null when the stroke doesn't match any shape confidently enough —
// the caller should keep it as raw ink in that case.
// ═════════════════════════════════════════════════════════════════════════════

// ── Result types ─────────────────────────────────────────────────────────────

export type ShapeKind = 'point' | 'line' | 'arrow' | 'double-arrow' | 'curve' | 'circle' | 'ellipse' | 'rectangle' | 'polygon';

export interface RecognisedVertex {
  x: number;
  y: number;
}

export interface RecognisedEdge {
  from: number;   // vertex index
  to: number;     // vertex index
  type: 'line' | 'arrow' | 'curve';
  controlPoints?: Point[];
}

export interface RecognisedShape {
  kind: ShapeKind;
  confidence: number;      // 0–1
  vertices: RecognisedVertex[];
  edges: RecognisedEdge[];
  bbox: BoundingBox;
  /** For circle/ellipse: center + radii. */
  center?: Point;
  rx?: number;
  ry?: number;
}

// ── Thresholds ───────────────────────────────────────────────────────────────

const LINE_MAX_DEVIATION_RATIO = 0.08;
const LINE_MIN_LENGTH = 20;
const ARROW_BEND_THRESHOLD = 42 * (Math.PI / 180);
const ARROW_MIN_TAIL_RATIO = 0.08;  // tail must be ≥ 8% of body length
const ARROW_TAIL_FRACTION = 0.15;
const ARROW_MIN_STRAIGHTNESS = 0.65;     // displacement/arcLength — reject scratchy strokes
const ARROW_BODY_MAX_DIR_VARIANCE = 0.15; // max direction variance along the body
const CLOSURE_DIST_RATIO = 0.18;    // start–end gap / arc-length (generous for hand-drawn)
const CIRCLE_RESIDUAL_RATIO = 0.10; // stricter circle — ellipse handles the rest
const CIRCLE_MAX_ASPECT_DEV = 0.45; // |1 - aspect| above this → not a circle
const ELLIPSE_RESIDUAL_RATIO = 0.12;
const CORNER_ANGLE_THRESHOLD = 50 * (Math.PI / 180); // per-point curvature threshold after smoothing — high to reject smooth arcs
const CORNER_MIN_SEPARATION = 0.10; // fraction of resampled points between corners (raised for cleaner separation)
const RECT_ANGLE_TOLERANCE = 25 * (Math.PI / 180);
const POLYGON_DEV_RATIO = 0.18;     // max avg edge deviation for polygon fit
const MIN_SHAPE_ARC = 30;           // px — below this, too small to recognise
const POINT_BBOX_TINY = 20;         // px bbox diagonal — always a point below this
const POINT_BBOX_MAX = 45;          // px bbox diagonal — point if also chaotic
const POINT_MAX_STRAIGHTNESS = 0.2; // displacement/arcLength below this = chaotic scribble

// Curve / arc detection
const CURVE_MIN_LENGTH = 25;                // minimum arc length for curve detection
const CURVE_MAX_RESIDUAL = 0.10;            // max mean-distance / arc-length for single-segment bezier
const CURVE_PIECEWISE_RESIDUAL = 0.04;      // target residual per segment in piecewise fit
const CURVE_MIN_STRAIGHTNESS = 0.30;        // reject chaotic scribbles
const CURVE_MAX_STRAIGHTNESS = 0.92;        // above this, fitLine should catch it
const CURVED_ARROW_MAX_BODY_RESIDUAL = 0.08; // max residual for curved arrow body

// ── Top-level recogniser ─────────────────────────────────────────────────────

/**
 * Attempt to recognise a geometric shape from a raw stroke.
 * Returns the best-matching shape, or null if confidence is too low.
 */
/**
 * Return ALL shape candidates with their confidence, sorted descending.
 * Used by the evaluate-invocation popup to show the top-N options.
 */
export function recogniseShapeCandidates(raw: StrokePoint[], maxResults = 5): RecognisedShape[] {
  if (raw.length < 4) return [];
  const feat = extractFeatures(raw);
  if (feat.arcLength < MIN_SHAPE_ARC) return [];

  const results: RecognisedShape[] = [];

  // Point
  const bboxDiag = Math.sqrt(feat.bbox.width ** 2 + feat.bbox.height ** 2);
  if (bboxDiag < POINT_BBOX_MAX) {
    results.push(fitPoint(feat));
  }

  const closeDist = Math.sqrt(
    (feat.endPoint.x - feat.startPoint.x) ** 2 +
    (feat.endPoint.y - feat.startPoint.y) ** 2,
  );
  const isClosed = closeDist / feat.arcLength < CLOSURE_DIST_RATIO * 1.5; // generous for candidates

  if (isClosed) {
    const corners = detectCorners(feat.resampled, feat.arcLength);
    if (corners.length >= 3) {
      const polygon = fitPolygon(feat, corners);
      if (polygon) results.push(polygon);
    }
    const circle = fitCircle(feat);
    if (circle) results.push(circle);
    const ellipse = fitEllipse(feat);
    if (ellipse) results.push(ellipse);
  }

  // Open shapes
  const dblArrow = fitDoubleArrow(feat);
  if (dblArrow) results.push(dblArrow);
  const arrow = fitArrow(feat);
  if (arrow) results.push(arrow);
  const curvedArrow = fitCurvedArrow(feat);
  if (curvedArrow) results.push(curvedArrow);
  const line = fitLine(feat);
  if (line) results.push(line);
  const curve = fitCurve(feat);
  if (curve) results.push(curve);

  results.sort((a, b) => b.confidence - a.confidence);
  return results.slice(0, maxResults);
}

export function recogniseShape(raw: StrokePoint[]): RecognisedShape | null {
  if (raw.length < 4) return null;
  const feat = extractFeatures(raw);
  if (feat.arcLength < MIN_SHAPE_ARC) return null;

  // ── Point: concentrated scribble in a small area ──────────────────────
  const bboxDiag = Math.sqrt(feat.bbox.width ** 2 + feat.bbox.height ** 2);
  if (bboxDiag < POINT_BBOX_TINY) {
    return fitPoint(feat);
  }
  if (bboxDiag < POINT_BBOX_MAX && feat.straightness < POINT_MAX_STRAIGHTNESS) {
    return fitPoint(feat);
  }

  const closeDist = Math.sqrt(
    (feat.endPoint.x - feat.startPoint.x) ** 2 +
    (feat.endPoint.y - feat.startPoint.y) ** 2,
  );
  const isClosed = closeDist / feat.arcLength < CLOSURE_DIST_RATIO;

  // Closed shapes — try smooth shapes (circle/ellipse) first, then polygon
  if (isClosed) {
    // Circle (strict — rejects elliptical shapes)
    const circle = fitCircle(feat);
    if (circle && circle.confidence >= 0.65) return circle;

    // Ellipse fallback (uses proper ellipse residual)
    const ellipse = fitEllipse(feat);
    if (ellipse && ellipse.confidence >= 0.55) return ellipse;

    // Polygon — only if we have real sharp corners
    const corners = detectCorners(feat.resampled, feat.arcLength);
    if (corners.length >= 3) {
      const polygon = fitPolygon(feat, corners);
      if (polygon && polygon.confidence >= 0.5) return polygon;
    }
  }

  // Open shapes
  const dblArrow = fitDoubleArrow(feat);
  if (dblArrow && dblArrow.confidence >= 0.55) return dblArrow;

  const arrow = fitArrow(feat);
  if (arrow && arrow.confidence >= 0.60) return arrow;

  const curvedArrow = fitCurvedArrow(feat);
  if (curvedArrow && curvedArrow.confidence >= 0.55) return curvedArrow;

  const line = fitLine(feat);
  if (line && line.confidence >= 0.55) return line;

  const curve = fitCurve(feat);
  if (curve && curve.confidence >= 0.45) return curve;

  return null;
}

// ── Stroke simplification threshold (M) ──────────────────────────────────────
// Determines whether a stroke is geometrically clean enough for vectorisation.
// Callers can use this to skip expensive recognition on chaotic scribbles.

export function isSimplifiable(raw: StrokePoint[]): boolean {
  if (raw.length < 4) return false;
  const feat = extractFeatures(raw);
  if (feat.arcLength < MIN_SHAPE_ARC) return false;
  // Concentrated scribble (point) — always simplifiable
  const bboxDiag = Math.sqrt(feat.bbox.width ** 2 + feat.bbox.height ** 2);
  if (bboxDiag < POINT_BBOX_MAX) return true;
  // Too many self-crossings = chaotic ink, not a clean shape
  if (feat.selfCrossings > 3) return false;
  // Very high winding (> 1.5 turns) without being a circle → spiral scribble
  if (feat.turn.absWinding > 1.5 && feat.straightness > 0.25) return false;
  return true;
}

// ── Point fitter ─────────────────────────────────────────────────────────────

function fitPoint(feat: StrokeFeatures): RecognisedShape {
  const cx = feat.bbox.x + feat.bbox.width / 2;
  const cy = feat.bbox.y + feat.bbox.height / 2;
  return {
    kind: 'point',
    confidence: 0.9,
    vertices: [{ x: cx, y: cy }],
    edges: [],
    bbox: { x: cx - 5, y: cy - 5, width: 10, height: 10 },
    center: { x: cx, y: cy },
  };
}

// ── Line fitter ──────────────────────────────────────────────────────────────

function fitLine(feat: StrokeFeatures): RecognisedShape | null {
  if (feat.arcLength < LINE_MIN_LENGTH) return null;
  if (feat.straightness < 1 - LINE_MAX_DEVIATION_RATIO) return null;

  // Also check max perpendicular deviation
  const s = feat.startPoint, e = feat.endPoint;
  const maxDev = maxPerpendicularDeviation(feat.resampled, s, e);
  if (maxDev / feat.arcLength > LINE_MAX_DEVIATION_RATIO) return null;

  const confidence = 0.5 + feat.straightness * 0.5;

  return {
    kind: 'line',
    confidence: Math.min(confidence, 1),
    vertices: [
      { x: s.x, y: s.y },
      { x: e.x, y: e.y },
    ],
    edges: [{ from: 0, to: 1, type: 'line' }],
    bbox: feat.bbox,
  };
}

function maxPerpendicularDeviation(pts: StrokePoint[], a: StrokePoint, b: StrokePoint): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-6) return 0;
  let maxD = 0;
  for (const p of pts) {
    const d = Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) / len;
    if (d > maxD) maxD = d;
  }
  return maxD;
}

// ── Body direction statistics (reusable for future line segmentation) ────────
//
// Computes direction angle variance and histogram over a sub-range of the
// resampled polyline.  Low variance = straight body; high = scratchy / wavy.

export interface DirectionStats {
  meanAngle: number;
  angleVariance: number;
  // 8-bin histogram of direction angles (each bin = 45°), normalised 0–1
  angularHistogram: Float64Array;
}

export function bodyDirectionStats(pts: StrokePoint[], startIdx: number, endIdx: number): DirectionStats {
  const hist = new Float64Array(8);
  let sumSin = 0, sumCos = 0;
  let count = 0;
  const angles: number[] = [];

  for (let i = startIdx; i < endIdx && i < pts.length - 1; i++) {
    const dx = pts[i + 1].x - pts[i].x;
    const dy = pts[i + 1].y - pts[i].y;
    if (dx * dx + dy * dy < 1e-8) continue;
    const a = Math.atan2(dy, dx);
    angles.push(a);
    sumSin += Math.sin(a);
    sumCos += Math.cos(a);
    // Bin: map [-π,π] → [0,8)
    const bin = Math.floor(((a + Math.PI) / TWO_PI) * 8) % 8;
    hist[bin]++;
    count++;
  }

  const meanAngle = Math.atan2(sumSin, sumCos);

  // Circular variance: 1 - resultant length / N
  const R = Math.sqrt(sumSin * sumSin + sumCos * sumCos);
  const angleVariance = count > 0 ? 1 - R / count : 0;

  // Normalise histogram
  if (count > 0) { for (let i = 0; i < 8; i++) hist[i] /= count; }

  return { meanAngle, angleVariance, angularHistogram: hist };
}

// ── Arrow fitter ─────────────────────────────────────────────────────────────
// A line where the last ~15% bends sharply — arrowhead.
// Hardened: requires overall straightness, low body direction variance.

function fitArrow(feat: StrokeFeatures): RecognisedShape | null {
  if (feat.arcLength < LINE_MIN_LENGTH) return null;

  // ── Global straightness gate — scratchy transversal strokes fail here
  if (feat.straightness < ARROW_MIN_STRAIGHTNESS) return null;

  const pts = feat.resampled;
  const n = pts.length;
  const tailStart = Math.floor(n * (1 - ARROW_TAIL_FRACTION));

  // Check that the body (first 85%) is straight
  const bodyPts = pts.slice(0, tailStart + 1);
  const bodyStart = bodyPts[0], bodyEnd = bodyPts[bodyPts.length - 1];
  const bodyLen = pathLength(bodyPts);
  if (bodyLen < LINE_MIN_LENGTH) return null;

  const bodyDev = maxPerpendicularDeviation(bodyPts, bodyStart, bodyEnd);
  if (bodyDev / bodyLen > LINE_MAX_DEVIATION_RATIO * 1.5) return null;

  // ── Body direction variance gate — reject strokes that wiggle laterally
  const dirStats = bodyDirectionStats(pts, 0, tailStart);
  if (dirStats.angleVariance > ARROW_BODY_MAX_DIR_VARIANCE) return null;

  // Check that the tail bends sharply and has real length
  const tailPts = pts.slice(tailStart);
  if (tailPts.length < 3) return null;

  const tailLen = pathLength(tailPts);
  if (tailLen / bodyLen < ARROW_MIN_TAIL_RATIO) return null;

  const bEnd = pts[tailStart];
  const bPrev = pts[Math.max(tailStart - 3, 0)];
  const bodyAngle = Math.atan2(bEnd.y - bPrev.y, bEnd.x - bPrev.x);

  const tEnd = pts[n - 1];
  const tailAngle = Math.atan2(tEnd.y - bEnd.y, tEnd.x - bEnd.x);
  const bend = Math.abs(wrapAngle(tailAngle - bodyAngle));

  if (bend < ARROW_BEND_THRESHOLD) return null;

  // Tail tip must actually displace sideways — reject gentle curves
  const tailDisp = Math.sqrt((tEnd.x - bEnd.x) ** 2 + (tEnd.y - bEnd.y) ** 2);
  if (tailDisp < 6) return null;

  const confidence = Math.min(0.7 + (bend / Math.PI) * 0.3, 0.95);

  return {
    kind: 'arrow',
    confidence,
    vertices: [
      { x: feat.startPoint.x, y: feat.startPoint.y },
      { x: bEnd.x, y: bEnd.y },
    ],
    edges: [{ from: 0, to: 1, type: 'arrow' }],
    bbox: feat.bbox,
  };
}

// ── Double-arrow fitter ──────────────────────────────────────────────────────
// Both ends (first 15% + last 15%) bend sharply; the middle 70% is the body.

function fitDoubleArrow(feat: StrokeFeatures): RecognisedShape | null {
  if (feat.arcLength < LINE_MIN_LENGTH * 1.5) return null;
  if (feat.straightness < ARROW_MIN_STRAIGHTNESS * 0.9) return null;

  const pts = feat.resampled;
  const n = pts.length;
  const headEnd = Math.ceil(n * ARROW_TAIL_FRACTION);    // first 15%
  const tailStart = Math.floor(n * (1 - ARROW_TAIL_FRACTION)); // last 15%

  // Body = middle portion
  const bodyPts = pts.slice(headEnd, tailStart + 1);
  if (bodyPts.length < 4) return null;
  const bodyStart = bodyPts[0], bodyEnd = bodyPts[bodyPts.length - 1];
  const bodyLen = pathLength(bodyPts);
  if (bodyLen < LINE_MIN_LENGTH) return null;

  const bodyDev = maxPerpendicularDeviation(bodyPts, bodyStart, bodyEnd);
  if (bodyDev / bodyLen > LINE_MAX_DEVIATION_RATIO * 1.5) return null;

  const dirStats = bodyDirectionStats(pts, headEnd, tailStart);
  if (dirStats.angleVariance > ARROW_BODY_MAX_DIR_VARIANCE) return null;

  // ── Check leading arrowhead (first 15%) ──────────────────────────────
  const headPts = pts.slice(0, headEnd + 1);
  if (headPts.length < 3) return null;
  const headLen = pathLength(headPts);
  if (headLen / bodyLen < ARROW_MIN_TAIL_RATIO) return null;

  const hBodyNext = pts[Math.min(headEnd + 3, tailStart)];
  const bodyAngleAtHead = Math.atan2(hBodyNext.y - pts[headEnd].y, hBodyNext.x - pts[headEnd].x);
  const headTip = pts[0];
  const headAngle = Math.atan2(headTip.y - pts[headEnd].y, headTip.x - pts[headEnd].x);
  const headBend = Math.abs(wrapAngle(headAngle - bodyAngleAtHead));
  if (headBend < ARROW_BEND_THRESHOLD) return null;
  const headDisp = Math.sqrt((headTip.x - pts[headEnd].x) ** 2 + (headTip.y - pts[headEnd].y) ** 2);
  if (headDisp < 6) return null;

  // ── Check trailing arrowhead (last 15%) ──────────────────────────────
  const tailPts = pts.slice(tailStart);
  if (tailPts.length < 3) return null;
  const tailLen = pathLength(tailPts);
  if (tailLen / bodyLen < ARROW_MIN_TAIL_RATIO) return null;

  const bPrev = pts[Math.max(tailStart - 3, headEnd)];
  const bodyAngleAtTail = Math.atan2(pts[tailStart].y - bPrev.y, pts[tailStart].x - bPrev.x);
  const tailTip = pts[n - 1];
  const tailAngle = Math.atan2(tailTip.y - pts[tailStart].y, tailTip.x - pts[tailStart].x);
  const tailBend = Math.abs(wrapAngle(tailAngle - bodyAngleAtTail));
  if (tailBend < ARROW_BEND_THRESHOLD) return null;
  const tailDisp = Math.sqrt((tailTip.x - pts[tailStart].x) ** 2 + (tailTip.y - pts[tailStart].y) ** 2);
  if (tailDisp < 6) return null;

  const avgBend = (headBend + tailBend) / 2;
  const confidence = Math.min(0.65 + (avgBend / Math.PI) * 0.3, 0.93);

  return {
    kind: 'double-arrow',
    confidence,
    vertices: [
      { x: feat.startPoint.x, y: feat.startPoint.y },
      { x: pts[headEnd].x, y: pts[headEnd].y },
      { x: pts[tailStart].x, y: pts[tailStart].y },
      { x: feat.endPoint.x, y: feat.endPoint.y },
    ],
    edges: [
      { from: 0, to: 1, type: 'arrow' },
      { from: 1, to: 2, type: 'line' },
      { from: 2, to: 3, type: 'arrow' },
    ],
    bbox: feat.bbox,
  };
}

// ── Circle fitter ────────────────────────────────────────────────────────────

function fitCircle(feat: StrokeFeatures): RecognisedShape | null {
  const pts = feat.resampled;

  // Must wind roughly once
  if (feat.turn.absWinding < 0.75) return null;

  // Reject very elliptical shapes — let fitEllipse handle them
  if (Math.abs(1 - feat.bboxAspect) > CIRCLE_MAX_ASPECT_DEV) return null;

  // Compute centroid
  let cx = 0, cy = 0;
  for (const p of pts) { cx += p.x; cy += p.y; }
  cx /= pts.length; cy /= pts.length;

  // Compute mean radius and residual
  let sumR = 0;
  for (const p of pts) {
    sumR += Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
  }
  const meanR = sumR / pts.length;
  if (meanR < 5) return null;

  let sumResid = 0;
  for (const p of pts) {
    const r = Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2);
    sumResid += Math.abs(r - meanR);
  }
  const residual = sumResid / pts.length / meanR;

  if (residual > CIRCLE_RESIDUAL_RATIO) return null;

  const confidence = Math.min(0.65 + (1 - residual) * 0.35, 0.95);

  const nVerts = 24;
  const verts: RecognisedVertex[] = [];
  const edges: RecognisedEdge[] = [];
  for (let i = 0; i < nVerts; i++) {
    const a = (i / nVerts) * TWO_PI;
    verts.push({ x: cx + meanR * Math.cos(a), y: cy + meanR * Math.sin(a) });
    edges.push({ from: i, to: (i + 1) % nVerts, type: 'line' });
  }
  return { kind: 'circle', confidence, vertices: verts, edges, bbox: feat.bbox, center: { x: cx, y: cy }, rx: meanR, ry: meanR };
}

// ── Ellipse fitter ───────────────────────────────────────────────────────────

function fitEllipse(feat: StrokeFeatures): RecognisedShape | null {
  const pts = feat.resampled;

  // Must wind at least ~290° — loose loops are not ellipses
  if (feat.turn.absWinding < 0.80) return null;

  const rx = feat.bbox.width / 2;
  const ry = feat.bbox.height / 2;
  if (rx < 8 || ry < 8) return null;
  const cx = feat.bbox.x + rx;
  const cy = feat.bbox.y + ry;

  // Compute ellipse residual: scaled distance of each point from the ellipse boundary
  let sumResid = 0;
  for (const p of pts) {
    const d = Math.sqrt(((p.x - cx) / rx) ** 2 + ((p.y - cy) / ry) ** 2);
    sumResid += Math.abs(d - 1);
  }
  const residual = sumResid / pts.length;

  if (residual > ELLIPSE_RESIDUAL_RATIO) return null;

  const confidence = Math.min(0.6 + (1 - residual) * 0.35, 0.92);

  const nVerts = 24;
  const verts: RecognisedVertex[] = [];
  const edges: RecognisedEdge[] = [];
  for (let i = 0; i < nVerts; i++) {
    const a = (i / nVerts) * TWO_PI;
    verts.push({ x: cx + rx * Math.cos(a), y: cy + ry * Math.sin(a) });
    edges.push({ from: i, to: (i + 1) % nVerts, type: 'line' });
  }
  return { kind: 'ellipse', confidence, vertices: verts, edges, bbox: feat.bbox, center: { x: cx, y: cy }, rx, ry };
}

// ── Polygon / rectangle fitter ───────────────────────────────────────────────

function fitPolygon(feat: StrokeFeatures, corners: number[]): RecognisedShape | null {
  const pts = feat.resampled;

  if (corners.length < 3 || corners.length > 8) return null;

  const verts: RecognisedVertex[] = corners.map(i => ({ x: pts[i].x, y: pts[i].y }));
  const edges: RecognisedEdge[] = [];
  for (let i = 0; i < verts.length; i++) {
    edges.push({ from: i, to: (i + 1) % verts.length, type: 'line' });
  }

  // Check if it's a rectangle (4 corners, ~90° angles)
  if (corners.length === 4) {
    const rectConf = rectangleConfidence(verts);
    if (rectConf > 0.65) {
      const snapped = snapRectangle(verts);
      return {
        kind: 'rectangle',
        confidence: rectConf,
        vertices: snapped.verts,
        edges,
        bbox: snapped.bbox,
      };
    }
  }

  // General polygon fitness: average deviation from ideal edges
  let totalDev = 0;
  let segCount = 0;
  for (let i = 0; i < corners.length; i++) {
    const vi = verts[i], vj = verts[(i + 1) % verts.length];
    const ci = corners[i], cj = corners[(i + 1) % corners.length];
    const segPts = ci < cj ? pts.slice(ci, cj + 1) : [...pts.slice(ci), ...pts.slice(0, cj + 1)];
    for (const p of segPts) {
      totalDev += pointToSegmentDist(p, vi, vj);
      segCount++;
    }
  }
  const avgDev = segCount > 0 ? totalDev / segCount : Infinity;
  const maxEdge = Math.max(feat.bbox.width, feat.bbox.height);
  const devRatio = maxEdge > 0 ? avgDev / maxEdge : 1;

  if (devRatio > POLYGON_DEV_RATIO) return null;

  const confidence = Math.min(0.55 + (1 - devRatio) * 0.4, 0.92);

  return { kind: 'polygon', confidence, vertices: verts, edges, bbox: feat.bbox };
}

/**
 * Detect dominant corners via Gaussian-smoothed curvature peaks on the resampled polyline.
 * For closed shapes, wraps the curvature computation around the closure seam so that
 * corners near the start/end of the stroke are not missed.
 */
function detectCorners(pts: StrokePoint[], _arcLen: number, isClosed = true): number[] {
  const n = pts.length;
  const minSep = Math.max(Math.floor(n * CORNER_MIN_SEPARATION), 3);

  // For closed shapes, create a wrapped buffer: append a few points from the
  // start at the end (and vice versa) so curvature/smoothing wraps correctly.
  const PAD = 4; // must be ≥ kernel half-width (2) + 1
  let ext: StrokePoint[];
  let offset: number; // how many extra points prepended
  if (isClosed && n > PAD * 2 + 4) {
    // Wrap: [...last PAD pts, ...original, ...first PAD pts]
    ext = [
      ...pts.slice(n - PAD),
      ...pts,
      ...pts.slice(0, PAD),
    ];
    offset = PAD;
  } else {
    ext = pts;
    offset = 0;
  }
  const en = ext.length;

  // Step 1: compute raw curvature angles on extended buffer
  const rawAngles: number[] = new Array(en).fill(0);
  for (let i = 1; i < en - 1; i++) {
    const ax = ext[i].x - ext[i - 1].x, ay = ext[i].y - ext[i - 1].y;
    const bx = ext[i + 1].x - ext[i].x, by = ext[i + 1].y - ext[i].y;
    const dot = ax * bx + ay * by;
    const cross = ax * by - ay * bx;
    rawAngles[i] = Math.abs(Math.atan2(cross, dot));
  }

  // Step 2: Gaussian smooth (kernel radius 2) to suppress jitter on straight edges
  const kernel = [0.06, 0.24, 0.40, 0.24, 0.06]; // σ≈1.0
  const smoothed: number[] = new Array(en).fill(0);
  for (let i = 2; i < en - 2; i++) {
    smoothed[i] = rawAngles[i - 2] * kernel[0] + rawAngles[i - 1] * kernel[1]
                + rawAngles[i] * kernel[2] + rawAngles[i + 1] * kernel[3]
                + rawAngles[i + 2] * kernel[4];
  }

  // Step 3: find peaks above threshold — only within the original [0, n) range
  const peaks: { idx: number; angle: number }[] = [];
  for (let i = offset + 1; i < offset + n - 1; i++) {
    if (smoothed[i] < CORNER_ANGLE_THRESHOLD) continue;
    const left = smoothed[i - 1];
    const right = smoothed[i + 1];
    if (smoothed[i] >= left && smoothed[i] >= right) {
      peaks.push({ idx: i - offset, angle: smoothed[i] });
    }
  }
  // Also check the closure seam point (index 0 / n-1) if closed
  if (isClosed && offset > 0) {
    const seamIdx = offset; // corresponds to pts[0]
    if (smoothed[seamIdx] >= CORNER_ANGLE_THRESHOLD &&
        smoothed[seamIdx] >= smoothed[seamIdx - 1] &&
        smoothed[seamIdx] >= smoothed[seamIdx + 1]) {
      peaks.push({ idx: 0, angle: smoothed[seamIdx] });
    }
    // Also the last original point (pts[n-1])
    const lastIdx = offset + n - 1;
    if (lastIdx < en - 2 &&
        smoothed[lastIdx] >= CORNER_ANGLE_THRESHOLD &&
        smoothed[lastIdx] >= smoothed[lastIdx - 1] &&
        smoothed[lastIdx] >= smoothed[lastIdx + 1]) {
      // Only add if not a duplicate of the seam corner
      if (!peaks.some(p => p.idx === n - 1 || (p.idx === 0 && Math.abs(n - 1 - 0) < minSep))) {
        peaks.push({ idx: n - 1, angle: smoothed[lastIdx] });
      }
    }
  }

  peaks.sort((a, b) => b.angle - a.angle);
  const kept: number[] = [];
  for (const pk of peaks) {
    // For closed shapes, distance wraps around
    const farEnough = kept.every(k => {
      const d = Math.abs(k - pk.idx);
      const dWrap = isClosed ? Math.min(d, n - d) : d;
      return dWrap >= minSep;
    });
    if (farEnough) {
      kept.push(pk.idx);
    }
  }
  kept.sort((a, b) => a - b);
  return kept;
}

function rectangleConfidence(verts: RecognisedVertex[]): number {
  if (verts.length !== 4) return 0;
  let totalAngleErr = 0;
  for (let i = 0; i < 4; i++) {
    const a = verts[i], b = verts[(i + 1) % 4], c = verts[(i + 2) % 4];
    const ba = { x: a.x - b.x, y: a.y - b.y };
    const bc = { x: c.x - b.x, y: c.y - b.y };
    const dot = ba.x * bc.x + ba.y * bc.y;
    const cross = ba.x * bc.y - ba.y * bc.x;
    const angle = Math.abs(Math.atan2(cross, dot));
    totalAngleErr += Math.abs(angle - Math.PI / 2);
  }
  const avgErr = totalAngleErr / 4;
  if (avgErr > RECT_ANGLE_TOLERANCE) return 0;
  return Math.min(0.7 + (1 - avgErr / RECT_ANGLE_TOLERANCE) * 0.3, 0.95);
}

function snapRectangle(verts: RecognisedVertex[]): { verts: RecognisedVertex[]; bbox: BoundingBox } {
  let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
  for (const v of verts) {
    if (v.x < mnX) mnX = v.x;
    if (v.y < mnY) mnY = v.y;
    if (v.x > mxX) mxX = v.x;
    if (v.y > mxY) mxY = v.y;
  }
  const diag1 = Math.atan2(verts[1].y - verts[0].y, verts[1].x - verts[0].x);
  const nearAxis = Math.abs(diag1 % (Math.PI / 2));
  if (nearAxis < 0.15 || nearAxis > Math.PI / 2 - 0.15) {
    return {
      verts: [
        { x: mnX, y: mnY }, { x: mxX, y: mnY },
        { x: mxX, y: mxY }, { x: mnX, y: mxY },
      ],
      bbox: { x: mnX, y: mnY, width: mxX - mnX, height: mxY - mnY },
    };
  }
  return {
    verts,
    bbox: { x: mnX, y: mnY, width: mxX - mnX, height: mxY - mnY },
  };
}

function pointToSegmentDist(p: StrokePoint | RecognisedVertex, a: RecognisedVertex, b: RecognisedVertex): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-10) return Math.sqrt((p.x - a.x) ** 2 + (p.y - a.y) ** 2);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const px = a.x + t * dx, py = a.y + t * dy;
  return Math.sqrt((p.x - px) ** 2 + (p.y - py) ** 2);
}

// ═════════════════════════════════════════════════════════════════════════════
// Bezier Curve Fitting
//
// Least-squares fitting of quadratic (1 control point) and cubic (2 control
// points) Bezier curves to a resampled stroke.  Chord-length parameterisation
// assigns tᵢ to each sample point; the control points are solved via normal
// equations.
// ═════════════════════════════════════════════════════════════════════════════

interface BezierFitResult {
  controlPoints: Point[];
  residual: number;  // mean distance / arc length (scale-invariant)
}

/** Assign cumulative chord-length parameters t ∈ [0, 1] to each point. */
function chordLengthParams(pts: StrokePoint[]): number[] {
  const n = pts.length;
  const t = new Array<number>(n);
  t[0] = 0;
  let total = 0;
  for (let i = 1; i < n; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    total += Math.sqrt(dx * dx + dy * dy);
    t[i] = total;
  }
  if (total > 1e-10) {
    for (let i = 1; i < n; i++) t[i] /= total;
  }
  return t;
}

/**
 * Fit a quadratic Bezier B(t) = (1-t)²P₀ + 2t(1-t)C + t²P₁
 * with P₀ = pts[0] and P₁ = pts[last].  Solves for C via least squares.
 */
function fitQuadBezier(pts: StrokePoint[], arcLen: number): BezierFitResult {
  const n = pts.length;
  const P0 = pts[0], P1 = pts[n - 1];
  const t = chordLengthParams(pts);

  let sww = 0, swrx = 0, swry = 0;
  for (let i = 1; i < n - 1; i++) {
    const ti = t[i];
    const w = 2 * ti * (1 - ti);
    const rx = pts[i].x - (1 - ti) * (1 - ti) * P0.x - ti * ti * P1.x;
    const ry = pts[i].y - (1 - ti) * (1 - ti) * P0.y - ti * ti * P1.y;
    sww += w * w;
    swrx += w * rx;
    swry += w * ry;
  }

  if (sww < 1e-10) {
    return { controlPoints: [{ x: (P0.x + P1.x) / 2, y: (P0.y + P1.y) / 2 }], residual: Infinity };
  }

  const cx = swrx / sww;
  const cy = swry / sww;

  let sumDist = 0;
  for (let i = 0; i < n; i++) {
    const ti = t[i];
    const u = 1 - ti;
    const bx = u * u * P0.x + 2 * u * ti * cx + ti * ti * P1.x;
    const by = u * u * P0.y + 2 * u * ti * cy + ti * ti * P1.y;
    sumDist += Math.sqrt((pts[i].x - bx) ** 2 + (pts[i].y - by) ** 2);
  }

  return { controlPoints: [{ x: cx, y: cy }], residual: sumDist / n / Math.max(arcLen, 1) };
}

/**
 * Fit a cubic Bezier B(t) = (1-t)³P₀ + 3t(1-t)²C₁ + 3t²(1-t)C₂ + t³P₃
 * with P₀ = pts[0] and P₃ = pts[last].  Solves for C₁, C₂ via 2×2 normal
 * equations (Cramer's rule).
 */
function fitCubicBezier(pts: StrokePoint[], arcLen: number): BezierFitResult {
  const n = pts.length;
  const P0 = pts[0], P3 = pts[n - 1];
  const t = chordLengthParams(pts);

  let saa = 0, sab = 0, sbb = 0;
  let sadx = 0, sady = 0, sbdx = 0, sbdy = 0;

  for (let i = 1; i < n - 1; i++) {
    const ti = t[i];
    const u = 1 - ti;
    const a = 3 * ti * u * u;
    const b = 3 * ti * ti * u;
    const dx = pts[i].x - u * u * u * P0.x - ti * ti * ti * P3.x;
    const dy = pts[i].y - u * u * u * P0.y - ti * ti * ti * P3.y;
    saa += a * a;
    sab += a * b;
    sbb += b * b;
    sadx += a * dx;
    sady += a * dy;
    sbdx += b * dx;
    sbdy += b * dy;
  }

  const det = saa * sbb - sab * sab;
  if (Math.abs(det) < 1e-10) {
    const mx = (P0.x + P3.x) / 2;
    const my = (P0.y + P3.y) / 2;
    return { controlPoints: [{ x: mx, y: my }, { x: mx, y: my }], residual: Infinity };
  }

  const c1x = (sbb * sadx - sab * sbdx) / det;
  const c1y = (sbb * sady - sab * sbdy) / det;
  const c2x = (saa * sbdx - sab * sadx) / det;
  const c2y = (saa * sbdy - sab * sady) / det;

  let sumDist = 0;
  for (let i = 0; i < n; i++) {
    const ti = t[i];
    const u = 1 - ti;
    const bx = u * u * u * P0.x + 3 * u * u * ti * c1x + 3 * u * ti * ti * c2x + ti * ti * ti * P3.x;
    const by = u * u * u * P0.y + 3 * u * u * ti * c1y + 3 * u * ti * ti * c2y + ti * ti * ti * P3.y;
    sumDist += Math.sqrt((pts[i].x - bx) ** 2 + (pts[i].y - by) ** 2);
  }

  return {
    controlPoints: [{ x: c1x, y: c1y }, { x: c2x, y: c2y }],
    residual: sumDist / n / Math.max(arcLen, 1),
  };
}

// ── Curve fitter ─────────────────────────────────────────────────────────────
// Detects open curves (arcs, bends).  Tries single quadratic / cubic first;
// if the residual is too high, splits the curve at high-curvature points and
// fits each segment independently (piecewise bezier).  This prevents complex
// S-curves from being over-simplified.

/** Find the index of maximum curvature in a sub-range of resampled points. */
function maxCurvatureIndex(pts: StrokePoint[], lo: number, hi: number): number {
  let best = lo + Math.floor((hi - lo) / 2);
  let bestK = 0;
  for (let i = lo + 1; i < hi - 1; i++) {
    const ax = pts[i].x - pts[i - 1].x, ay = pts[i].y - pts[i - 1].y;
    const bx = pts[i + 1].x - pts[i].x, by = pts[i + 1].y - pts[i].y;
    const cross = Math.abs(ax * by - ay * bx);
    const denom = Math.sqrt((ax * ax + ay * ay) * (bx * bx + by * by));
    const k = denom > 1e-8 ? cross / denom : 0;
    if (k > bestK) { bestK = k; best = i; }
  }
  return best;
}

/** Fit a single segment (quad or cubic), return best result. */
function fitSegmentBezier(
  pts: StrokePoint[],
): { controlPoints: Point[]; residual: number } {
  const arc = pathLength(pts);
  const quad = fitQuadBezier(pts, arc);
  const cubic = fitCubicBezier(pts, arc);
  return quad.residual <= cubic.residual * 1.2 ? quad : cubic;
}

function fitCurve(feat: StrokeFeatures): RecognisedShape | null {
  if (feat.arcLength < CURVE_MIN_LENGTH) return null;
  if (feat.straightness > CURVE_MAX_STRAIGHTNESS) return null;   // too straight — line
  if (feat.straightness < CURVE_MIN_STRAIGHTNESS) return null;   // too chaotic
  if (feat.selfCrossings > 1) return null;

  // Skip closed shapes — circle/ellipse/polygon handle those
  const closeDist = Math.sqrt(
    (feat.endPoint.x - feat.startPoint.x) ** 2 +
    (feat.endPoint.y - feat.startPoint.y) ** 2,
  );
  if (closeDist / feat.arcLength < CLOSURE_DIST_RATIO) return null;

  const pts = feat.resampled;

  // ── Try single-segment fit first ──────────────────────────────────
  const singleFit = fitSegmentBezier(pts);

  if (singleFit.residual <= CURVE_PIECEWISE_RESIDUAL) {
    // Great fit with a single bezier
    const confidence = Math.min(
      0.55 + (1 - singleFit.residual / CURVE_MAX_RESIDUAL) * 0.35,
      0.88,
    );
    return buildCurveShape(feat, [{ pts, fit: singleFit }], confidence);
  }

  // ── Piecewise: split at max curvature, fit each half ──────────────
  // Try 2 segments, then 3 if still poor.
  const segments2 = splitAndFit(pts, 2);
  const avgRes2 = segments2.reduce((s, seg) => s + seg.fit.residual, 0) / segments2.length;

  if (avgRes2 <= CURVE_PIECEWISE_RESIDUAL) {
    const confidence = Math.min(0.52 + (1 - avgRes2 / CURVE_MAX_RESIDUAL) * 0.36, 0.86);
    return buildCurveShape(feat, segments2, confidence);
  }

  // Try 3 segments for S-curves and complex shapes
  const segments3 = splitAndFit(pts, 3);
  const avgRes3 = segments3.reduce((s, seg) => s + seg.fit.residual, 0) / segments3.length;

  if (avgRes3 <= CURVE_PIECEWISE_RESIDUAL * 1.5) {
    const confidence = Math.min(0.50 + (1 - avgRes3 / CURVE_MAX_RESIDUAL) * 0.34, 0.84);
    return buildCurveShape(feat, segments3, confidence);
  }

  // Still a single-segment fit within the looser max residual?
  if (singleFit.residual <= CURVE_MAX_RESIDUAL) {
    const confidence = Math.min(
      0.45 + (1 - singleFit.residual / CURVE_MAX_RESIDUAL) * 0.30,
      0.78,
    );
    return buildCurveShape(feat, [{ pts, fit: singleFit }], confidence);
  }

  return null;
}

interface CurveSegment { pts: StrokePoint[]; fit: { controlPoints: Point[]; residual: number } }

/** Split resampled points into N segments at high-curvature points and fit each. */
function splitAndFit(pts: StrokePoint[], nSegments: number): CurveSegment[] {
  // Find split points by recursively splitting at max curvature
  const splits = [0];
  const ranges: [number, number][] = [[0, pts.length - 1]];

  while (splits.length < nSegments) {
    // Find the range with the worst fit
    let worstIdx = 0;
    let worstRes = -1;
    for (let i = 0; i < ranges.length; i++) {
      const [lo, hi] = ranges[i];
      if (hi - lo < 6) continue; // too small to split
      const seg = pts.slice(lo, hi + 1);
      const res = fitSegmentBezier(seg).residual;
      if (res > worstRes) { worstRes = res; worstIdx = i; }
    }
    const [lo, hi] = ranges[worstIdx];
    if (hi - lo < 6) break; // can't split further
    const mid = maxCurvatureIndex(pts, lo, hi);
    if (mid <= lo + 2 || mid >= hi - 2) break; // split point too close to edge
    ranges.splice(worstIdx, 1, [lo, mid], [mid, hi]);
    splits.push(mid);
  }

  // Sort ranges and fit each segment
  ranges.sort((a, b) => a[0] - b[0]);
  return ranges.map(([lo, hi]) => {
    const seg = pts.slice(lo, hi + 1);
    return { pts: seg, fit: fitSegmentBezier(seg) };
  });
}

/** Build a RecognisedShape from one or more fitted bezier segments. */
function buildCurveShape(
  feat: StrokeFeatures,
  segments: CurveSegment[],
  confidence: number,
): RecognisedShape {
  const vertices: RecognisedVertex[] = [];
  const edges: RecognisedEdge[] = [];

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const start = seg.pts[0];
    const end = seg.pts[seg.pts.length - 1];

    if (i === 0) {
      vertices.push({ x: start.x, y: start.y });
    }
    vertices.push({ x: end.x, y: end.y });

    edges.push({
      from: i,
      to: i + 1,
      type: 'curve',
      controlPoints: seg.fit.controlPoints,
    });
  }

  return {
    kind: 'curve',
    confidence,
    vertices,
    edges,
    bbox: feat.bbox,
  };
}

// ── Curved arrow fitter ──────────────────────────────────────────────────────
// Like fitArrow but the body can follow a curved path.  Only matches when the
// body deviates too much for the straight arrow fitter.

function fitCurvedArrow(feat: StrokeFeatures): RecognisedShape | null {
  if (feat.arcLength < LINE_MIN_LENGTH) return null;
  if (feat.straightness < 0.30) return null;          // too chaotic
  if (feat.selfCrossings > 2) return null;

  const pts = feat.resampled;
  const n = pts.length;
  const tailStart = Math.floor(n * (1 - ARROW_TAIL_FRACTION));

  // Check arrowhead bend
  const tailPts = pts.slice(tailStart);
  if (tailPts.length < 3) return null;

  const bodyPts = pts.slice(0, tailStart + 1);
  const bodyLen = pathLength(bodyPts);
  const tailLen = pathLength(tailPts);
  if (bodyLen < LINE_MIN_LENGTH) return null;
  if (tailLen / bodyLen < ARROW_MIN_TAIL_RATIO) return null;

  const bEnd = pts[tailStart];
  const bPrev = pts[Math.max(tailStart - 3, 0)];
  const bodyAngle = Math.atan2(bEnd.y - bPrev.y, bEnd.x - bPrev.x);

  const tEnd = pts[n - 1];
  const tailAngle = Math.atan2(tEnd.y - bEnd.y, tEnd.x - bEnd.x);
  const bend = Math.abs(wrapAngle(tailAngle - bodyAngle));

  if (bend < ARROW_BEND_THRESHOLD) return null;

  const tailDisp = Math.sqrt((tEnd.x - bEnd.x) ** 2 + (tEnd.y - bEnd.y) ** 2);
  if (tailDisp < 6) return null;

  // Body must actually curve — if it's straight enough for regular fitArrow, skip
  const bodyDev = maxPerpendicularDeviation(bodyPts, bodyPts[0], bodyPts[bodyPts.length - 1]);
  if (bodyDev / bodyLen <= LINE_MAX_DEVIATION_RATIO) return null;    // straight body → fitArrow

  // Fit Bezier to the body
  const bodyFit = fitQuadBezier(bodyPts, bodyLen);
  if (bodyFit.residual > CURVED_ARROW_MAX_BODY_RESIDUAL) return null;

  const confidence = Math.min(
    0.50 + (bend / Math.PI) * 0.25 + (1 - bodyFit.residual / CURVED_ARROW_MAX_BODY_RESIDUAL) * 0.15,
    0.90,
  );

  return {
    kind: 'arrow',
    confidence,
    vertices: [
      { x: feat.startPoint.x, y: feat.startPoint.y },
      { x: bEnd.x, y: bEnd.y },
    ],
    edges: [{ from: 0, to: 1, type: 'arrow', controlPoints: bodyFit.controlPoints }],
    bbox: feat.bbox,
  };
}
